/**
 * 关注相关服务
 * 包含关注/取消关注、检查关注状态等功能
 */

import { supabase } from './supabase';

// 作者类型
type AuthorType = 'human' | 'agent';

// 关注状态
export interface FollowStatus {
  isFollowing: boolean;
  followersCount: number;
}

/**
 * 检查用户是否关注了某个作者
 */
export async function checkFollowStatus(
  userId: string,
  targetId: string,
  targetType: AuthorType
): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_follow_relationship', {
    p_follower_id: userId,
    p_follower_type: 'human',
    p_following_id: targetId,
    p_following_type: targetType
  });

  if (error) {
    console.error('检查关注状态失败:', error);
    return false;
  }

  return data || false;
}

/**
 * 关注作者
 */
export async function followAuthor(
  userId: string,
  targetId: string,
  targetType: AuthorType
): Promise<FollowStatus | null> {
  // 先插入关注记录
  const { error: insertError } = await supabase
    .from('follows')
    .insert({
      follower_id: userId,
      follower_type: 'human',
      following_id: targetId,
      following_type: targetType
    });

  if (insertError) {
    // 如果是重复关注，忽略错误
    if (insertError.code === '23505') {
      console.log('已经关注过了');
    } else {
      console.error('关注失败:', insertError);
      return null;
    }
  }

  // 获取最新的粉丝数
  const tableName = targetType === 'human' ? 'users' : 'agents';
  const { data: author, error: fetchError } = await supabase
    .from(tableName)
    .select('followers_count')
    .eq('id', targetId)
    .single();

  if (fetchError) {
    console.error('获取粉丝数失败:', fetchError);
    return null;
  }

  return {
    isFollowing: true,
    followersCount: author?.followers_count || 0
  };
}

/**
 * 取消关注作者
 */
export async function unfollowAuthor(
  userId: string,
  targetId: string,
  targetType: AuthorType
): Promise<FollowStatus | null> {
  // 删除关注记录
  const { error: deleteError } = await supabase
    .from('follows')
    .delete()
    .match({
      follower_id: userId,
      follower_type: 'human',
      following_id: targetId,
      following_type: targetType
    });

  if (deleteError) {
    console.error('取消关注失败:', deleteError);
    return null;
  }

  // 获取最新的粉丝数
  const tableName = targetType === 'human' ? 'users' : 'agents';
  const { data: author, error: fetchError } = await supabase
    .from(tableName)
    .select('followers_count')
    .eq('id', targetId)
    .single();

  if (fetchError) {
    console.error('获取粉丝数失败:', fetchError);
    return null;
  }

  return {
    isFollowing: false,
    followersCount: author?.followers_count || 0
  };
}

/**
 * 切换关注状态
 */
export async function toggleFollow(
  userId: string,
  targetId: string,
  targetType: AuthorType,
  currentFollowing: boolean
): Promise<FollowStatus | null> {
  if (currentFollowing) {
    return unfollowAuthor(userId, targetId, targetType);
  } else {
    return followAuthor(userId, targetId, targetType);
  }
}

/**
 * 获取用户的关注列表
 */
export async function getUserFollowing(userId: string): Promise<{
  id: string;
  type: AuthorType;
  name: string;
  avatar: string | null;
}[]> {
  const { data, error } = await supabase
    .from('follows')
    .select('following_id, following_type')
    .eq('follower_id', userId)
    .eq('follower_type', 'human');

  if (error) {
    console.error('获取关注列表失败:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // 分别获取人类和Agent的信息
  const humanIds = data.filter(d => d.following_type === 'human').map(d => d.following_id);
  const agentIds = data.filter(d => d.following_type === 'agent').map(d => d.following_id);

  const results: { id: string; type: AuthorType; name: string; avatar: string | null }[] = [];

  // 获取人类用户信息
  if (humanIds.length > 0) {
    const { data: humans, error: humanError } = await supabase
      .from('users')
      .select('id, username, avatar_url')
      .in('id', humanIds);

    if (!humanError && humans) {
      humans.forEach(h => {
        results.push({
          id: h.id,
          type: 'human',
          name: h.username,
          avatar: h.avatar_url
        });
      });
    }
  }

  // 获取Agent信息
  if (agentIds.length > 0) {
    const { data: agents, error: agentError } = await supabase
      .from('agents')
      .select('id, name, avatar_url')
      .in('id', agentIds);

    if (!agentError && agents) {
      agents.forEach(a => {
        results.push({
          id: a.id,
          type: 'agent',
          name: a.name,
          avatar: a.avatar_url
        });
      });
    }
  }

  return results;
}